<template>
  <div class="billing-customers">
    <EntraPageHeader
      title="CSP Customers"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadData"
    />

    <div class="page-body">
      <div class="filters-bar">
        <div class="filter-group">
          <label class="filter-label">Region:</label>
          <select v-model="selectedRegion" class="filter-select" @change="onRegionChange">
            <option value="">Select Region</option>
            <option v-for="region in regions" :key="region.region_code" :value="region.region_code">
              {{ region.region_code }}
            </option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Search:</label>
          <input
            v-model="searchInput"
            type="text"
            class="filter-input"
            placeholder="Customer name or domain..."
            @input="onSearchInput"
          />
        </div>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-else-if="!selectedRegion" class="empty-wrapper">
        <EmptyState title="Select a region" description="Choose a billing region to view customers." />
      </div>

      <div v-else-if="tableState === 'loading'" class="loading-state">
        <span>Loading customers...</span>
      </div>

      <div v-else-if="tableState === 'empty'" class="empty-wrapper">
        <EmptyState title="No customers found" description="No customers match the current search." />
      </div>

      <div v-else class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Domain</th>
              <th>Country</th>
              <th>First Seen</th>
              <th>Last Seen</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="customer in customers" :key="customer.customer_id + customer.region_code">
              <td>{{ customer.customer_name || 'Unknown' }}</td>
              <td>{{ customer.customer_domain || '--' }}</td>
              <td>{{ customer.customer_country || '--' }}</td>
              <td>{{ formatDate(customer.first_seen_at, 'short') }}</td>
              <td>{{ formatDate(customer.last_seen_at, 'short') }}</td>
              <td>
                <button class="summary-btn" @click="viewSummary(customer)">Summary</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button class="page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
          <span class="page-info">Page {{ page }} of {{ totalPages }}</span>
          <button class="page-btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
        </div>
      </div>

      <!-- Customer Summary Modal -->
      <transition name="modal-fade">
      <div v-if="summaryVisible" class="modal-overlay" @click="closeSummary">
        <div class="modal-content" @click.stop role="dialog" aria-modal="true" :aria-label="summaryModalTitle">
          <div class="modal-header">
            <div>
              <h2>{{ summaryModalTitle }}</h2>
              <div v-if="summaryCustomer && summaryCustomer.customer_domain" class="modal-subtitle">{{ summaryCustomer.customer_domain }}</div>
            </div>
            <button class="modal-close" @click="closeSummary" aria-label="Close summary">&times;</button>
          </div>
          <div v-if="summaryState === 'loading'" class="loading-state">
            <span>Loading summary...</span>
          </div>
          <div v-else-if="summaryState === 'error'" class="error-message">
            {{ summaryError }}
          </div>
          <div v-else class="modal-body">
            <div class="summary-kpi-grid">
              <MetricCard
                label="Total Spend"
                :value="formatCurrency(summary.summary?.total_spend, summaryCurrency || undefined)"
                :state="summaryState"
              />
              <MetricCard
                label="Latest Month"
                :value="latestMonthTotal !== null ? formatCurrency(latestMonthTotal, summaryCurrency || undefined) : '--'"
                :state="summaryState"
                :trendDirection="summaryMonthTrend.direction"
                :trendLabel="summaryMonthTrend.label"
              />
              <MetricCard label="Invoices" :value="summary.summary?.invoice_count || 0" :state="summaryState" />
              <MetricCard label="Products" :value="summary.summary?.product_count || 0" :state="summaryState" />
              <MetricCard label="Last Billed" :value="formatDate(summary.summary?.last_billed, 'short')" :state="summaryState" />
            </div>

            <div class="chart-section">
              <h3>Monthly Spend Trend</h3>
              <div v-if="summaryMonthlyLabels.length > 0" class="chart-wrapper">
                <BillingBarChart
                  :labels="summaryMonthlyLabels"
                  :values="summaryMonthlyValues"
                  label="Spend"
                  :currency="summaryCurrency"
                  :auto-skip="false"
                />
              </div>
              <p v-else class="no-data">No monthly data available</p>
            </div>

            <div class="chart-section">
              <h3>Spend by Product</h3>
              <div v-if="summaryProductLabels.length > 0" class="chart-wrapper-doughnut">
                <BillingDoughnutChart
                  :labels="summaryProductLabels"
                  :values="summaryProductValues"
                  :currency="summaryCurrency"
                />
              </div>
              <p v-else class="no-data">No product breakdown available</p>
            </div>

            <h3>Top Products</h3>
            <table v-if="summary.topProducts && summary.topProducts.length > 0" class="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Total</th>
                  <th>Seats / Usage</th>
                  <th>Currency</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, idx) in summary.topProducts" :key="idx">
                  <td>{{ product.product_name || '--' }}</td>
                  <td>{{ product.sku_name || '--' }}</td>
                  <td>{{ formatCurrency(product.total, product.currency) }}</td>
                  <td>{{ formatProductQuantity(product) }}</td>
                  <td>{{ product.currency || '--' }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="no-data">No product data available</p>

            <h3>Monthly Spend (Detail)</h3>
            <table v-if="summaryMonthlyDesc.length > 0" class="data-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Total</th>
                  <th>Currency</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(month, idx) in summaryMonthlyDesc" :key="idx">
                  <td>{{ formatMonth(month.billing_month) }}</td>
                  <td>{{ formatCurrency(month.total, month.currency) }}</td>
                  <td>{{ month.currency || '--' }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="no-data">No monthly data available</p>
          </div>
        </div>
      </div>
      </transition>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import EmptyState from '@/components/entra/common/EmptyState.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import BillingBarChart from '@/components/billing/BillingBarChart.vue';
import BillingDoughnutChart from '@/components/billing/BillingDoughnutChart.vue';
import { getBillingRegions, getBillingCustomers, getBillingCustomerSummary } from '@/services/billing-api.js';
import { formatCurrency, formatCount } from '@/services/entra-utils.js';
import { formatDate } from '@/services/date-utils.js';

export default {
  name: 'BillingCustomers',
  components: { EntraPageHeader, EmptyState, MetricCard, BillingBarChart, BillingDoughnutChart },
  data: function() {
    return {
      regions: [],
      selectedRegion: '',
      customers: [],
      tableState: 'loading',
      searchInput: '',
      searchDebounce: null,
      page: 1,
      totalPages: 1,
      pageSize: 50,
      error: '',
      summaryVisible: false,
      summaryState: 'loading',
      summaryError: '',
      summary: {},
      summaryCustomer: null,
      loadToken: 0,
      summaryRequestedId: ''
    };
  },
  computed: {
    summaryModalTitle: function() {
      if (this.summaryCustomer && this.summaryCustomer.customer_name) {
        return this.summaryCustomer.customer_name;
      }
      return 'Customer Summary';
    },
    summaryMonthlySorted: function() {
      if (!this.summary.monthlySpend) return [];
      return this.summary.monthlySpend.slice().sort(function(a, b) {
        return String(a.billing_month).localeCompare(String(b.billing_month));
      });
    },
    summaryMonthlyDesc: function() {
      return this.summaryMonthlySorted.slice().reverse();
    },
    summaryMonthlyLabels: function() {
      var self = this;
      return this.summaryMonthlySorted.map(function(m) { return self.formatMonth(m.billing_month); });
    },
    summaryMonthlyValues: function() {
      return this.summaryMonthlySorted.map(function(m) { return Number(m.total || 0); });
    },
    latestMonthTotal: function() {
      var monthly = this.summaryMonthlySorted;
      if (monthly.length === 0) return null;
      return monthly[monthly.length - 1].total;
    },
    summaryMonthTrend: function() {
      var empty = { direction: '', label: '' };
      var monthly = this.summaryMonthlySorted;
      if (monthly.length < 2) return empty;
      var prev = Number(monthly[monthly.length - 2].total || 0);
      var current = Number(monthly[monthly.length - 1].total || 0);
      if (prev <= 0) return empty;
      var pct = ((current - prev) / prev) * 100;
      return {
        direction: pct > 0 ? 'up' : (pct < 0 ? 'down' : 'neutral'),
        label: (pct > 0 ? '+' : '') + pct.toFixed(1) + '% vs ' + this.formatMonth(monthly[monthly.length - 2].billing_month)
      };
    },
    summaryCurrency: function() {
      if (this.summary.monthlySpend && this.summary.monthlySpend.length > 0) {
        return this.summary.monthlySpend[0].currency || '';
      }
      if (this.summary.topProducts && this.summary.topProducts.length > 0) {
        return this.summary.topProducts[0].currency || '';
      }
      return '';
    },
    summaryProductSlices: function() {
      if (!this.summary.topProducts) return [];
      var slices = this.summary.topProducts.map(function(p) {
        return { name: p.product_name || 'Unknown', total: Number(p.total || 0) };
      });
      var MAX_SLICES = 7;
      if (slices.length <= MAX_SLICES + 1) return slices;
      var otherTotal = 0;
      var rest = slices.slice(MAX_SLICES);
      for (var i = 0; i < rest.length; i++) otherTotal += rest[i].total;
      slices = slices.slice(0, MAX_SLICES);
      slices.push({ name: 'Other (' + rest.length + ' products)', total: otherTotal });
      return slices;
    },
    summaryProductLabels: function() {
      return this.summaryProductSlices.map(function(s) { return s.name; });
    },
    summaryProductValues: function() {
      return this.summaryProductSlices.map(function(s) { return s.total; });
    }
  },
  methods: {
    formatCurrency: function(amount, currency) {
      return formatCurrency(amount, currency);
    },
    formatCount: formatCount,
    formatProductQuantity: function(product) {
      var seats = Number(product.licensed_seats);
      if (product.licensed_seats !== null && product.licensed_seats !== undefined && !isNaN(seats)) {
        return formatCount(Math.round(seats)) + ' seats';
      }
      var units = Number(product.usage_units);
      if (product.usage_units !== null && product.usage_units !== undefined && !isNaN(units)) {
        var label = { '1 Hour': 'hours', '1/Hour': 'hours', '1 GB/Month': 'GB-months', '1/Month': 'months' }[product.usage_unit_label] || product.usage_unit_label || 'units';
        return formatCount(Math.round(units)) + ' ' + label;
      }
      return '--';
    },
    formatDate: function(dateStr, mode) {
      return formatDate(dateStr, { mode: mode || 'short' });
    },
    formatMonth: function(monthStr) {
      if (!monthStr) return '--';
      var d = new Date(monthStr);
      if (isNaN(d.getTime())) return '--';
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months[d.getUTCMonth()] + " '" + String(d.getUTCFullYear()).slice(2);
    },
    loadRegions: function() {
      var self = this;
      getBillingRegions().then(function(data) {
        self.regions = data.items || [];
      }).catch(function(err) {
        console.error('Failed to load billing regions:', err);
      });
    },
    onRegionChange: function() {
      this.page = 1;
      this.loadData();
    },
    onSearchInput: function() {
      var self = this;
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(function() {
        self.page = 1;
        self.loadData();
      }, 300);
    },
    loadData: function() {
      var self = this;
      if (!this.selectedRegion) {
        this.tableState = 'loaded';
        return;
      }

      var token = ++this.loadToken;
      this.error = '';
      this.tableState = 'loading';

      var params = {
        page: this.page,
        pageSize: this.pageSize
      };
      if (this.searchInput) params.search = this.searchInput;

      getBillingCustomers(this.selectedRegion, params).then(function(data) {
        if (token !== self.loadToken) return;
        self.customers = data.items || [];
        self.totalPages = Math.ceil((data.total || 0) / self.pageSize);
        self.tableState = self.customers.length === 0 ? 'empty' : 'loaded';
      }).catch(function(err) {
        if (token !== self.loadToken) return;
        console.error('Failed to load customers:', err);
        self.error = err.message || 'Failed to load customer data.';
        self.tableState = 'error';
      });
    },
    goToPage: function(newPage) {
      this.page = newPage;
      this.loadData();
    },
    viewSummary: function(customer) {
      var self = this;
      var customerId = customer.customer_id;
      this.summaryVisible = true;
      this.summaryState = 'loading';
      this.summaryError = '';
      this.summary = {};
      this.summaryCustomer = customer;
      this.summaryRequestedId = customerId;

      getBillingCustomerSummary(this.selectedRegion, customerId).then(function(data) {
        if (self.summaryRequestedId !== customerId) return;
        self.summary = data;
        self.summaryState = 'loaded';
      }).catch(function(err) {
        if (self.summaryRequestedId !== customerId) return;
        self.summaryError = err.message || 'Failed to load customer summary.';
        self.summaryState = 'error';
      });
    },
    closeSummary: function() {
      this.summaryVisible = false;
    },
    onSummaryKeydown: function(event) {
      if (event.key === 'Escape') this.closeSummary();
    }
  },
  watch: {
    summaryVisible: function(visible) {
      if (visible) {
        document.addEventListener('keydown', this.onSummaryKeydown);
        document.body.style.overflow = 'hidden';
      } else {
        document.removeEventListener('keydown', this.onSummaryKeydown);
        document.body.style.overflow = '';
      }
    },
    '$route.params.regionCode': function(newRegion) {
      if (newRegion) {
        this.selectedRegion = newRegion.toUpperCase();
        this.page = 1;
        this.loadData();
      }
    }
  },
  mounted: function() {
    this.loadRegions();
    var routeRegion = this.$route.params.regionCode;
    if (routeRegion) {
      this.selectedRegion = routeRegion.toUpperCase();
    }
    if (this.selectedRegion) {
      this.loadData();
    }
  },
  beforeUnmount: function() {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    document.removeEventListener('keydown', this.onSummaryKeydown);
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
.billing-customers {
  min-height: 100%;
}

.page-body {
  padding: 24px;
}

.filters-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.filter-select, .filter-input {
  padding: 6px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #334155;
  background: #ffffff;
  font-family: 'Inter', sans-serif;
}

.filter-input {
  width: 240px;
}

.table-wrapper {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.data-table th {
  text-align: left;
  padding: 10px 12px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #e2e8f0;
}

.data-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.data-table tbody tr:hover {
  background: #f8fafc;
}

.summary-btn {
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
}
.summary-btn:hover { background: #f1f5f9; border-color: #94a3b8; }

.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  padding: 16px;
}

.page-btn {
  padding: 6px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #334155;
  font-size: 0.8125rem;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-btn:hover:not(:disabled) { background: #f1f5f9; }

.page-info {
  font-size: 0.8125rem;
  color: #64748b;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #ffffff;
  border-radius: 8px;
  width: 95%;
  max-width: 1100px;
  max-height: 92vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.modal-subtitle {
  font-size: 0.8125rem;
  color: #64748b;
  margin-top: 2px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.15s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}
.modal-close:hover { color: #475569; }

.modal-body {
  padding: 20px;
}

.chart-section h3 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.chart-section {
  margin-bottom: 24px;
}

.chart-wrapper {
  height: 360px;
  position: relative;
  width: 100%;
}

.chart-wrapper-doughnut {
  height: 300px;
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.no-data {
  font-size: 0.8125rem;
  color: #94a3b8;
  text-align: center;
  padding: 24px;
}

.summary-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.summary-kpi-grid :deep(.metric-value) {
  font-size: 1.25rem;
  word-break: break-word;
}

.modal-body h3 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
  margin: 16px 0 8px 0;
}

.modal-body .data-table {
  margin-bottom: 16px;
}

.empty-wrapper {
  display: flex;
  justify-content: center;
  padding: 48px;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 16px;
  border-radius: 8px;
  font-size: 0.875rem;
}

.loading-state {
  text-align: center;
  padding: 48px;
  color: #94a3b8;
  font-size: 0.875rem;
}
</style>
